import DashboardShell from "@/components/DashboardShell";
import PrintLogoHeader from "@/components/PrintLogoHeader";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Printer, MessageCircle, Users, Database, CreditCard, Building2, Shield, Mail, Cloud, Server } from "lucide-react";

const NOW = [
      { Icon: MessageCircle, title: "Automatic SMS campaign", body: "Day 7 + Day 15 texts fire nightly during your quiet-hours window — the exact days and % are pulled live from your Settings." },
  { Icon: Users, title: "Per-advisor scorecards", body: "Rank the team by conversion — coach from real data." },
  { Icon: Database, title: "Any DMS, any format", body: "Nightly CSV upload with auto-detected columns." },
];

const Q2 = [
  { Icon: MessageCircle, title: "Live Twilio delivery + two-way replies", body: "Real texts to customer phones + capture STOP / questions back into the pipeline." },
  { Icon: Users, title: "Advisor logins with role-based access", body: "Each advisor sees only their own customers and scorecard." },
  { Icon: Cloud, title: "DealerVault / Authenticom feed", body: "One agreement, works with 90% of US DMS systems. Dealer signs a data consent, we handle the rest — CSV lands in our SFTP box every night, zero dealer effort." },
];

const Q3 = [
  { Icon: CreditCard, title: "Stripe ACH billing", body: "Dealer pays their statement in one click. No checks to chase." },
  { Icon: Building2, title: "Multi-rooftop / white-label", body: "One login sees every store in the group; group-level analytics." },
  { Icon: Shield, title: "TCPA compliance suite", body: "Quiet-hours enforcement, opt-out log, retention policies." },
];

const Q4 = [
  { Icon: Database, title: "Direct DMS integration", body: "CDK, Reynolds, Tekion — certified API connectors, real-time push." },
  { Icon: Mail, title: "Weekly advisor emails", body: "Every Monday each advisor gets a scorecard of their own numbers." },
];

function Section({ label, quarter, items, badgeClass }) {
  return (
    <Card className="p-6 border-border mb-6" data-testid={`roadmap-${quarter.toLowerCase().replace(" ", "-")}`}>
      <div className="flex items-center gap-3 mb-4">
        <Badge className={badgeClass}>{label}</Badge>
        <div className="font-display font-bold text-xl tracking-tight">{quarter}</div>
      </div>
      <div className="grid md:grid-cols-3 gap-5">
        {items.map(({ Icon, title, body }, i) => (
          <div key={i} className="flex gap-3">
            <div className="w-9 h-9 rounded-md bg-navy text-white grid place-items-center shrink-0">
              <Icon className="w-4 h-4" strokeWidth={2} />
            </div>
            <div>
              <div className="font-display font-bold text-sm">{title}</div>
              <div className="text-xs text-muted-foreground mt-1 leading-relaxed">{body}</div>
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}

export default function RoadmapPage() {
  return (
    <DashboardShell>
      <PrintLogoHeader docTitle="12-Month Roadmap" />
      <div className="mb-6 flex items-start justify-between gap-4 no-print">
        <div>
          <div className="font-mono-label text-muted-foreground">Product</div>
          <h1 className="font-display text-4xl font-bold tracking-tighter mt-1">Roadmap</h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-xl">
            What&apos;s shipping over the next 12 months. Hand this to any dealer who asks &quot;is this a
            side project or a real company?&quot;
          </p>
        </div>
        <Button onClick={() => window.print()} data-testid="print-roadmap-btn">
          <Printer className="w-4 h-4 mr-2" strokeWidth={1.75} />
          Print
        </Button>
      </div>

      <Section label="LIVE NOW" quarter="Q1 2026" items={NOW} badgeClass="bg-emerald-600 text-white" />
      <Section label="IN DEVELOPMENT" quarter="Q2 2026" items={Q2} badgeClass="bg-[hsl(var(--gold))] text-neutral-900" />
      <Section label="COMMITTED" quarter="Q3 2026" items={Q3} badgeClass="bg-blue-600 text-white" />
      <Section label="PLANNED" quarter="Q4 2026" items={Q4} badgeClass="bg-stone-500 text-white" />

      {/* DMS Data Flow section — explains cloud pull to dealers */}
      <Card className="p-6 border-border mb-6" data-testid="roadmap-data-flow">
        <div className="flex items-center gap-3 mb-4">
          <Badge className="bg-navy text-white">DATA FLOW</Badge>
          <div className="font-display font-bold text-xl tracking-tight">How Decline Drive gets your customer data</div>
        </div>
        <p className="text-sm text-muted-foreground mb-5 max-w-3xl">
          Most dealerships don&apos;t want to run reports every morning — and they shouldn&apos;t have to.
          Decline Drive supports three tiers of data delivery. We recommend the middle tier for 90% of stores.
        </p>

        <div className="grid md:grid-cols-3 gap-5">
          {/* Tier 1 */}
          <div className="border border-border rounded-md p-4">
            <div className="flex items-center gap-2 mb-2">
              <Badge className="bg-emerald-600 text-white text-[0.6rem]">TIER 1 · CLOUD API</Badge>
            </div>
            <div className="flex gap-3">
              <div className="w-9 h-9 rounded-md bg-emerald-600 text-white grid place-items-center shrink-0">
                <Cloud className="w-4 h-4" strokeWidth={2} />
              </div>
              <div>
                <div className="font-display font-bold text-sm">Direct cloud DMS API</div>
                <div className="text-xs text-muted-foreground mt-1 leading-relaxed">
                  Tekion, Dominion VUE, PBS, DealerSocket, Dealertrack, Xtime, DealerBuilt — dealer signs a 5-minute API consent form, data flows nightly forever. No dealer effort after signup.
                </div>
              </div>
            </div>
          </div>

          {/* Tier 2 */}
          <div className="border-2 border-[hsl(var(--gold))] rounded-md p-4 bg-[hsl(var(--gold))]/5">
            <div className="flex items-center gap-2 mb-2">
              <Badge className="bg-[hsl(var(--gold))] text-neutral-900 text-[0.6rem]">TIER 2 · RECOMMENDED</Badge>
            </div>
            <div className="flex gap-3">
              <div className="w-9 h-9 rounded-md bg-[hsl(var(--gold))] text-neutral-900 grid place-items-center shrink-0">
                <Server className="w-4 h-4" strokeWidth={2} />
              </div>
              <div>
                <div className="font-display font-bold text-sm">DMS aggregator feed</div>
                <div className="text-xs text-muted-foreground mt-1 leading-relaxed">
                  <b>DealerVault / Authenticom</b>, Fortellis, or IntegraLink pulls from CDK, Reynolds, Auto/Mate, Dealertrack — every legacy DMS in the country. Delivered to our SFTP box nightly. Dealer effort: sign one consent form.
                </div>
              </div>
            </div>
          </div>

          {/* Tier 3 */}
          <div className="border border-border rounded-md p-4">
            <div className="flex items-center gap-2 mb-2">
              <Badge className="bg-stone-500 text-white text-[0.6rem]">TIER 3 · FALLBACK</Badge>
            </div>
            <div className="flex gap-3">
              <div className="w-9 h-9 rounded-md bg-stone-500 text-white grid place-items-center shrink-0">
                <Database className="w-4 h-4" strokeWidth={2} />
              </div>
              <div>
                <div className="font-display font-bold text-sm">Manual CSV upload</div>
                <div className="text-xs text-muted-foreground mt-1 leading-relaxed">
                  For small independents on legacy DMS with no cloud pull. Office manager drops the daily Declined Services report into our app — takes 30 seconds. Already live today.
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-6 rounded-md bg-muted/60 border border-border p-4">
          <div className="font-mono-label text-muted-foreground mb-1">Onboarding pitch</div>
          <div className="text-sm text-foreground/90 italic leading-relaxed">
            &quot;We handle the DMS data pull as part of onboarding. Sign the data consent form, we work with
            DealerVault to establish a nightly feed to our platform, and you never touch it again. Setup takes
            about a week and is included in your onboarding fee. From then on, every declined RO written today
            gets a text in 7 days — automatically.&quot;
          </div>
        </div>
      </Card>

      <div className="text-xs text-muted-foreground border-t border-border pt-4 max-w-3xl mt-4">
        Roadmap dates are targets, not guarantees. Priority is driven by dealer feedback — the more stores on the
        platform, the faster items ship. © {new Date().getFullYear()} Decline Drive™ by Ayers Enterprises.
      </div>
    </DashboardShell>
  );
}
